/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

import {SystemServiceName} from '../util/interfaces';

import {ModelResponse, ResponseData} from '@dvrd/dvr-controls';

import {getSystemServices, toggleSystemService} from '../util/requestSender';

interface Props extends BaseProps {
    name?: SystemServiceName;
    active?: boolean;
}

interface Fields extends BaseFields {
    name: SystemServiceName;
    active: boolean;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    name: SystemServiceName.CONNECTION,
    active: true
};

const fieldsDefinition: {[key in keyof Fields]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    name: createFieldDef(ModelFieldType.STRING),
    active: createFieldDef(ModelFieldType.BOOLEAN)
};

const ignoreProps: Array<keyof Props> = [];

export default class SystemService extends BaseModel<Props, Fields> {
    static getAll = (callback: ModelResponse<SystemService[]>) => {
        getSystemServices({
            callback: (data: ResponseData) => {
                const services: SystemService[] = data.success
                    ? data.services.map((service: any) => new SystemService(service))
                    : [];
                callback(services, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build();
    }

    get id(): string {
        return this.fields.id as string;
    }

    get name(): SystemServiceName {
        return this.fields.name;
    }

    get translatedName(): string {
        switch (this.name) {
            case SystemServiceName.CONNECTION:
                return 'API connectie controle';
            case SystemServiceName.HISTORY:
                return 'Historische facturen verzenden';
            case SystemServiceName.MAILCHIMP:
                return 'Mailchimp synchronisatie';
            case SystemServiceName.MAILERLITE_SYNC_SERVICE:
                return 'MailerLite synchronisatie';
            case SystemServiceName.PENDING:
                return 'Te verzenden facturen verzenden';
            case SystemServiceName.PERIODIC:
                return 'Periodiek importeren';
            case SystemServiceName.THREAD:
                return 'Thread logs opruimen';
            case SystemServiceName.PRODUCT_IMPORTER:
                return 'Producten importeren';
            case SystemServiceName.EMAIL_SEQUENCE:
                return 'Email sequences (DynamoDB)';
            case SystemServiceName.RETURN_IMPORT:
                return 'Retouren importeren';
            case SystemServiceName.PLANNED_PROCESS_EXECUTOR:
                return 'Uitvoeren geplande processen';
            case SystemServiceName.INVOICE_REQUEST_IMPORTER:
                return 'Factuuraanvragen importeren';
            case SystemServiceName.BOL_PRODUCT_SYNC:
                return 'Bol producten synchroniseren';
            case SystemServiceName.RATINGS_IMPORTER:
                return 'Ratings importeren';
            case SystemServiceName.NOTIFICATION_SENDER:
                return 'Notificaties verzenden';
        }
    }

    set name(name: SystemServiceName) {
        this.fields.name = name;
    }

    get active(): boolean {
        return this.fields.active;
    }

    set active(active: boolean) {
        this.fields.active = active;
    }

    toggle = (callback: ModelResponse<SystemService>) => {
        toggleSystemService({
            data: {service_name: this.name, enabled: !this.active},
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.service);
                callback(this, data.success, data);
            }
        });
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
    };
}
